// Client-side session resolution. Replaces the React `cache()`-wrapped
// server helpers in the old `server-session.ts`. One canonical query key
// (`['me', 'context']`) — every consumer routes through the same
// TanStack Query subscription, so router guards, layouts, and component
// hooks share a single network call per session window.

import { queryOptions, useQuery, type QueryClient } from '@tanstack/react-query';
import { redirect } from '@tanstack/react-router';
import { asUserId, asSessionId } from '@leanin/shared/ids';
import { photoUrlFromKey } from '@leanin/shared/media-url';
import type { AuthedSession } from '@leanin/shared/auth/session';
import type { AuthDriftReason } from '@leanin/shared/auth/drift';
import { SELF_REACTIVATE_WINDOW_MS } from '@leanin/shared/auth/windows';
import type { PlatformRole } from '@leanin/shared/views/profile';
import { getApiClient } from './api-client';
import { throwApiError } from './api-error';

export type Member = {
  id: string;
  firstName: string;
  lastName: string;
  bio: string | null;
  photoStorageKey: string | null;
  photoStatus: string;
  platformRole: PlatformRole | null;
  emailVerifiedAt: Date | null;
  onboardingCompletedAt: Date | null;
  restricted: boolean;
  deactivatedAt: Date | null;
};

// Binary context. The server's auth middleware Just-In-Time provisions
// the local member row on first authed request for a new Clerk user
// (see `apps/server/src/identity/service/load-session.ts`), so the
// SPA never sees an "orphan" state — either we have an authed session
// or we don't.
export type MyContext = { session: AuthedSession; member: Member } | null;

function isUsable(m: Member): boolean {
  if (m.restricted) return false;
  if (m.deactivatedAt !== null) {
    const elapsed = Date.now() - m.deactivatedAt.getTime();
    if (elapsed > SELF_REACTIVATE_WINDOW_MS) return false;
  }
  return true;
}

// Clerk attaches itself to `window.Clerk` once the SDK initialises (see
// ClerkProvider in `main.tsx`). Typed loosely here — we only need
// `signOut` + a session-presence probe, and importing the SDK type pulls
// in heavy types we don't otherwise need at the api-client layer.
type ClerkLike = {
  readonly session?: unknown;
  signOut: () => Promise<void>;
};

function parseAuthState(error: unknown): AuthDriftReason | null {
  const inner = (error as { error?: { authState?: unknown } } | undefined)?.error;
  if (inner === null || inner === undefined) return null;
  const v = inner.authState;
  return v === 'restricted' || v === 'deactivated_expired' ? v : null;
}

// Reconcile when the SDK and the server disagree on a drift-final state.
// Drift-final = the server positively identified that the local member
// row is non-recoverable (restricted or hard-deactivated past the
// self-reactivate window) AND the caller had a valid Clerk JWT.
// Dropping the SDK session here is the right move because the Clerk
// session can no longer back a usable app session. For every OTHER
// 401 (transient backend failure, Clerk outage, plain anonymous,
// unknown) we leave the SDK alone — server-side flakiness should not
// destructively sign the user out on first occurrence.
function reconcileStaleClerkSession(): void {
  if (typeof window === 'undefined') return;
  const clerk = (window as unknown as { Clerk?: ClerkLike }).Clerk;
  if (!clerk?.session) return;
  document.cookie = 'dev_user_id=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
  void clerk.signOut();
}

async function fetchMyContext(): Promise<MyContext> {
  const client = getApiClient();
  const { data, error, response } = await client.GET('/api/v1/me/context');
  if (!data) {
    if (response.status === 401) {
      // Per Option B of the recovery-design: discriminate on the
      // server-emitted `authState`. Reconcile (destructively drop the
      // Clerk SDK session) ONLY when the server has positively
      // identified a drift-final cause. Bare UNAUTHENTICATED — the
      // transient case — returns null without reconciling, so the next
      // poll picks the session back up cleanly when the upstream
      // recovers. (Today's failure mode: a single Clerk Backend API
      // outage or DB blip otherwise destructively logged every
      // signed-in user out across the deployment.)
      const authState = parseAuthState(error);
      if (authState !== null) reconcileStaleClerkSession();
      return null;
    }
    if (response.status === 404) return null;
    throwApiError(error);
  }
  const raw = data.member;
  const member: Member = {
    id: raw.id,
    firstName: raw.firstName,
    lastName: raw.lastName,
    bio: raw.bio ?? null,
    photoStorageKey: raw.photoStorageKey ?? null,
    photoStatus: raw.photoStatus,
    platformRole: raw.platformRole ?? null,
    emailVerifiedAt: raw.emailVerifiedAt != null ? new Date(raw.emailVerifiedAt) : null,
    onboardingCompletedAt:
      raw.onboardingCompletedAt != null ? new Date(raw.onboardingCompletedAt) : null,
    restricted: raw.restricted,
    deactivatedAt: raw.deactivatedAt != null ? new Date(raw.deactivatedAt) : null,
  };
  if (!isUsable(member)) return null;
  const s = data.session;
  const session: AuthedSession = {
    sessionId: asSessionId(s.sessionId),
    userId: asUserId(s.userId),
    platformRole: s.platformRole ?? null,
    emailVerified: s.emailVerified,
    stepUpValidUntil: s.stepUpValidUntil != null ? new Date(s.stepUpValidUntil) : null,
    absoluteExpiresAt: new Date(s.absoluteExpiresAt),
    idleExpiresAt: new Date(s.idleExpiresAt),
  };
  return { session, member };
}

export const sessionQuery = queryOptions({
  queryKey: ['me', 'context'] as const,
  queryFn: fetchMyContext,
  staleTime: 60_000,
});

export function useSession() {
  return useQuery(sessionQuery);
}

// Inverse of the `_authed` guard: if the visitor already has a usable
// session, bounce them to /feed. Used by `/auth/login`, `/auth/signup`,
// and `/auth/password-reset` — those pages don't make sense for signed-in
// users (and Clerk rejects `signIn.create()` calls while signed in).
//
// Mirrors `_authed.tsx`'s try/redirect split: redirect() returns a
// `Response` instance, so we isolate the fetch and throw outside the try.
export async function redirectIfSignedIn(context: {
  queryClient: QueryClient;
}): Promise<void> {
  let ctx: MyContext;
  try {
    ctx = await context.queryClient.ensureQueryData(sessionQuery);
  } catch {
    return;
  }
  if (ctx !== null) throw redirect({ to: '/feed' });
}

// Onboarding gate. A signed-in visitor whose `onboardingCompletedAt` is
// null hasn't finished the persona tour — they get bounced to
// `/onboarding/profile`. Anonymous visitors are passed through (the
// gate only fires when `ctx` is non-null).
//
// Lives here so both `_authed` (signed-in-only) AND `_public` (auth-
// optional but bounces signed-in incomplete users into onboarding)
// share a single definition. A new invitee whose webhook redeem
// granted them the leader role but who closed the tab before tour
// completion would otherwise be able to navigate directly to
// `/networks/{theirId}` (a `_public` route) and skip the tour for
// good — only `LoginForm` re-routed them, and only if they happened
// to revisit `/auth/login`.
//
// `/onboarding/*` and `/auth/*` aren't under either layout, so this
// gate never fires there — preventing the redirect loop the early
// drafts of this rule produced.
export function gateOnboardingComplete(ctx: MyContext): void {
  if (ctx === null) return;
  if (ctx.member.onboardingCompletedAt === null) {
    throw redirect({ to: '/onboarding/profile' });
  }
}

// Inverse of `gateOnboardingComplete`, applied by the `/onboarding`
// layout: a signed-in visitor who has ALREADY finished onboarding has no
// business on the `/onboarding/*` flow — bounce them to /feed. The pair
// makes routing self-correcting (incomplete → pulled INTO onboarding,
// complete → pushed OUT), so a stale session cache or a back-button can
// never strand a finished user on the profile step. No loop risk: the
// `/onboarding/*` subtree is disjoint from `_authed`/`_public`, so any
// given route is subject to only one of the two gates. Anonymous visitors
// (`ctx` null) pass through — the onboarding steps own their unauth'd
// state. Every step stamps `onboardingCompletedAt` only on its final-screen
// click before navigating to /feed, so this never fires mid-tour.
export function gateOnboardingIncomplete(ctx: MyContext): void {
  if (ctx === null) return;
  if (ctx.member.onboardingCompletedAt !== null) {
    throw redirect({ to: '/feed' });
  }
}

export function useMember(): Member | null {
  const { data } = useSession();
  return data?.member ?? null;
}

export function useAuthedSession(): AuthedSession | null {
  const { data } = useSession();
  return data?.session ?? null;
}

export function memberPhotoUrl(m: Member): string | undefined {
  return photoUrlFromKey(m.photoStorageKey, m.photoStatus) ?? undefined;
}

export function viewerForShell(m: Member): { name: string; avatarSrc?: string } {
  const name = `${m.firstName} ${m.lastName}`.trim();
  const photo = memberPhotoUrl(m);
  return photo !== undefined ? { name, avatarSrc: photo } : { name };
}

// Resource authoring is admin-only — mirrors the server-side
// `authorizeCreate` in `apps/server/src/resources/service/create-resource.ts`.
export function mayCreateResource(platformRole: PlatformRole | null): boolean {
  return platformRole === 'admin';
}
